import React, { useEffect, useState } from 'react';
import { X, Calendar, Clock, MapPin, User, FileText, Stethoscope, Save, RotateCcw } from 'lucide-react';
import { patientService } from '../services/patientService';

const EditBookingPopup = ({ booking, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    reason: '',
    appointment_type: '',
    schedule_type: '',
    specialty: '',
    doctor: '',
    doctor_id: '',
    clinic_location: '',
    appointment_date: '',
    appointment_time: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // State for specialties and doctors
  const [specialties, setSpecialties] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  // State for clinics
  const [availableClinics, setAvailableClinics] = useState([]);
  const [showClinicDropdown, setShowClinicDropdown] = useState(false);
  const [loadingClinics, setLoadingClinics] = useState(false);

  // State for available dates and times
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // Initialize form data when booking changes
  useEffect(() => {
    if (booking) {
      setFormData({
        reason: booking.reason || '',
        appointment_type: booking.appointment_type || '',
        schedule_type: booking.schedule_type || '',
        specialty: booking.specialty || '',
        doctor: booking.doctor || '',
        doctor_id: booking.doctor_id || '',
        clinic_location: booking.clinic_location || '',
        appointment_date: booking.appointment_date ? formatDateForInput(booking.appointment_date) : '',
        appointment_time: booking.appointment_time || ''
      });
    }
  }, [booking, isOpen]);

  // Fetch specialties when popup opens
  useEffect(() => {
    if (isOpen) {
      fetchSpecialties();
    }
  }, [isOpen]);

  // Fetch all clinics when popup opens (independent of doctor selection)
  useEffect(() => {
    if (isOpen) {
      const fetchAllClinics = async () => {
        try {
          setLoadingClinics(true);
          const clinics = await patientService.getAllClinics();
          setAvailableClinics(clinics);
        } catch (error) {
          console.error('Error fetching all clinics:', error);
          setAvailableClinics([]);
        } finally {
          setLoadingClinics(false);
        }
      };
      
      fetchAllClinics();
    }
  }, [isOpen]);

  // Fetch doctors when specialty changes
  useEffect(() => {
    if (formData.specialty) {
      fetchDoctorsBySpecialty(formData.specialty);
    } else {
      setDoctors([]);
      setFormData(prev => ({ ...prev, doctor: '', doctor_id: '' }));
    }
  }, [formData.specialty]);

  // Fetch available dates when doctor is set (including initial load and changes)
  useEffect(() => {
    if (formData.doctor_id) {
      fetchAvailableDates(formData.doctor_id);
    } else {
      setAvailableDates([]);
      setAvailableTimes([]);
    }
  }, [formData.doctor_id]);

  // Fetch available times when doctor or date changes
  useEffect(() => {
    if (formData.doctor_id && formData.appointment_date) {
      fetchAvailableTimes(formData.doctor_id, formData.appointment_date);
    } else {
      setAvailableTimes([]);
    }
  }, [formData.doctor_id, formData.appointment_date]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && event.target.classList.contains('popup-overlay')) {
        onClose();
      }
      if (showDoctorDropdown && !event.target.closest('.doctor-dropdown-container')) {
        setShowDoctorDropdown(false);
      }
      if (showClinicDropdown && !event.target.closest('.clinic-dropdown-container')) {
        setShowClinicDropdown(false);
      }
    };

    // Close popup when pressing Escape key
    const handleEscapeKey = (event) => {
      if (isOpen && event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset'; // Restore scrolling
    };
  }, [isOpen, onClose, showDoctorDropdown, showClinicDropdown]);

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Format time for input field (HH:MM)
  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const formatTimeSlot = (timeSlot) => {
    const [hours, minutes] = timeSlot.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleInputChange = (field, value) => {
    const updatedForm = { ...formData, [field]: value };
    
    // Auto-set schedule type based on appointment type
    if (field === 'appointment_type') {
      if (value === 'Consultation') {
        updatedForm.schedule_type = 'In Person';
      } else if (value === 'General Telehealth' || value === 'Clinical Telehealth') {
        updatedForm.schedule_type = 'Telehealth';
      } else {
        updatedForm.schedule_type = '';
      }
    }
    
    setFormData(updatedForm);
  };

  const fetchSpecialties = async () => {
    try {
      setLoadingSpecialties(true);
      const specialtiesData = await patientService.getSpecialties();
      setSpecialties(specialtiesData);
    } catch (error) {
      console.error('Error fetching specialties:', error);
      // Fallback to default specialties if API fails
      setSpecialties(['Cardiology', 'Internal Medicine', 'Pediatrics', 'Family Medicine', 'Orthopedics', 'Sports Medicine', 'Dermatology', 'Cosmetic Surgery', 'Neurology', 'Psychiatry']);
    } finally {
      setLoadingSpecialties(false);
    }
  };

  const fetchDoctorsBySpecialty = async (specialty) => {
    try {
      setLoadingDoctors(true);
      const doctorsData = await patientService.getDoctorsBySpecialty(specialty);
      setDoctors(doctorsData);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setDoctors([]);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const handleSpecialtyChange = (specialty) => {
    setFormData(prev => ({ 
      ...prev, 
      specialty: specialty,
      doctor: '', // Reset doctor when specialty changes
      doctor_id: ''
    }));
    setShowDoctorDropdown(false);
  };

  const handleDoctorSelect = async (doctor) => {
    try {
      // Update doctor information (clinics are already loaded globally)
      setFormData(prev => ({ 
        ...prev, 
        doctor: `${doctor.firstname} ${doctor.lastname}`,
        doctor_id: doctor.doctor_id
        // Keep current clinic selection - no need to reset
      }));

      setShowDoctorDropdown(false);
    } catch (error) {
      console.error('Error handling doctor selection:', error);
    }
  };

  const handleClinicSelect = (clinic) => {
    setFormData(prev => ({
      ...prev,
      clinic_location: clinic.clinic_name
    }));
    setShowClinicDropdown(false);
  };

  const toggleDoctorDropdown = () => {
    if (doctors.length > 0) {
      setShowDoctorDropdown(!showDoctorDropdown);
    }
  };

  const toggleClinicDropdown = () => {
    if (availableClinics.length > 0) {
      setShowClinicDropdown(!showClinicDropdown);
    }
  };

  // Fetch available dates for the selected doctor (optimized using month availability)
  const fetchAvailableDates = async (doctorId) => {
    try {
      setLoadingAvailability(true);
      if (!doctorId) {
        setAvailableDates([]);
        return;
      }

      const doctor = await patientService.getDoctorByNumericId(doctorId);
      const today = new Date();
      const dates = [];

      // Get availability for current month and next month
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();
      const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
      const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;

      try {
        // Fetch current month availability
        const currentMonthAvailability = await patientService.getDoctorMonthAvailability(doctor.id, currentYear, currentMonth);
        if (currentMonthAvailability && currentMonthAvailability.days) {
          currentMonthAvailability.days.forEach(day => {
            const dayDate = new Date(day.avail_date);
            dayDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            
            // Only include future dates with available slots
            if (dayDate >= today && day.is_available && day.available_slots_count > 0) {
              dates.push(day.avail_date);
            }
          });
        }
      } catch (error) {
        console.log('Current month availability not found:', error.message);
      }

      try {
        // Fetch next month availability
        const nextMonthAvailability = await patientService.getDoctorMonthAvailability(doctor.id, nextYear, nextMonth);
        if (nextMonthAvailability && nextMonthAvailability.days) {
          nextMonthAvailability.days.forEach(day => {
            const dayDate = new Date(day.avail_date);
            if (day.is_available && day.available_slots_count > 0) {
              dates.push(day.avail_date);
            }
          });
        }
      } catch (error) {
        console.log('Next month availability not found:', error.message);
      }

      // Sort dates and limit to next 30 days
      dates.sort();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      
      const filteredDates = dates.filter(date => {
        const dateObj = new Date(date);
        return dateObj <= thirtyDaysFromNow;
      });

      setAvailableDates(filteredDates);
    } catch (error) {
      console.error('Error fetching available dates:', error);
      setAvailableDates([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  // Filter past time slots for today's date
  const filterPastSlotsForToday = (times, selectedDate) => {
    // Only filter if the selected date is today
    const today = new Date();
    const selected = new Date(selectedDate);
    
    // Check if selected date is today (compare date only)
    const isToday = today.toDateString() === selected.toDateString();
    
    if (!isToday) {
      return times; // Return all times for non-today dates
    }
    
    // For today, filter out past time slots
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    return times.filter(time => {
      if (!time) return false;
      
      const [slotHour, slotMinute] = time.split(':').map(Number);
      const slotTimeInMinutes = slotHour * 60 + slotMinute;
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      
      // Keep slots that start after current time
      return slotTimeInMinutes > currentTimeInMinutes;
    });
  };

  // Fetch available times for the selected doctor and date
  const fetchAvailableTimes = async (doctorId, date) => {
    try {
      if (!doctorId || !date) {
        setAvailableTimes([]);
        return;
      }

      const doctor = await patientService.getDoctorByNumericId(doctorId);
      const slots = await patientService.getDoctorDaySlots(doctor.id, date);
      const times = (slots || [])
        .filter(s => s.status === 'AVAILABLE')
        .map(s => s.start_time.slice(0, 5));
      
      // Filter out past time slots for today
      const filteredTimes = filterPastSlotsForToday(times, new Date(date));
      setAvailableTimes(filteredTimes);
    } catch (error) {
      console.error('Error fetching available times:', error);
      setAvailableTimes([]);
    }
  };

  const handleSave = async () => {
    if (!booking?.id) {
      setError('Booking ID not found');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call the update service with booking ID and update data
      await patientService.updatePatientBooking(booking.id, formData);
      
      // Call the onSave callback to refresh the parent component
      if (onSave) {
        onSave();
      }
      
      // Close the popup
      onClose();
    } catch (error) {
      console.error('Error updating booking:', error);
      setError(error.message || 'Failed to update booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (booking) {
      setFormData({
        reason: booking.reason || '',
        appointment_type: booking.appointment_type || '',
        schedule_type: booking.schedule_type || '',
        specialty: booking.specialty || '',
        doctor: booking.doctor || '',
        doctor_id: booking.doctor_id || '',
        clinic_location: booking.clinic_location || '',
        appointment_date: booking.appointment_date ? formatDateForInput(booking.appointment_date) : '',
        appointment_time: booking.appointment_time || ''
      });
    }
    setError('');
    setShowDoctorDropdown(false);
    setShowClinicDropdown(false);
    onClose();
  };

  if (!isOpen || !booking) return null;

  return (
    <div className="popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Booking Details</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3 max-h-[calc(85vh-180px)] overflow-y-auto">
          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Booking ID (Read-only) */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <FileText className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Booking ID</span>
            </div>
            <p className="text-base font-semibold text-gray-900">{booking.booking_id || 'N/A'}</p>
          </div>

          {/* Main Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reason for Appointment */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Reason for Appointment</span>
              </div>
              <textarea
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                rows="2"
                placeholder="Enter reason for appointment"
              />
            </div>

            {/* Appointment Type */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Stethoscope className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Appointment Type</span>
              </div>
              <select
                value={formData.appointment_type}
                onChange={(e) => handleInputChange('appointment_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Select Type</option>
                <option value="General Telehealth">General Telehealth</option>
                <option value="Clinical Telehealth">Clinical Telehealth</option>
                <option value="Consultation">Consultation</option>
              </select>
            </div>

            {/* Schedule Type */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Schedule Type</span>
              </div>
              <select
                value={formData.schedule_type}
                onChange={(e) => handleInputChange('schedule_type', e.target.value)}
                disabled={formData.appointment_type !== ''}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                  formData.appointment_type !== '' ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select Schedule</option>
                <option value="In Person">In Person</option>
                <option value="Telehealth">Telehealth</option>
              </select>
            </div>

                         {/* Specialty */}
             <div className="space-y-1">
               <div className="flex items-center space-x-2">
                 <Stethoscope className="h-4 w-4 text-blue-600" />
                 <span className="text-sm font-medium text-gray-600">Specialty</span>
               </div>
               <select
                 value={formData.specialty}
                 onChange={(e) => handleSpecialtyChange(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
               >
                 <option value="">Select Specialty</option>
                 {loadingSpecialties ? (
                   <option value="" disabled>Loading specialties...</option>
                 ) : (
                   specialties.map((specialty) => (
                     <option key={specialty} value={specialty}>
                       {specialty}
                     </option>
                   ))
                 )}
               </select>
             </div>

                         {/* Doctor Name */}
             <div className="space-y-1">
               <div className="flex items-center space-x-2">
                 <User className="h-4 w-4 text-blue-600" />
                 <span className="text-sm font-medium text-gray-600">Doctor Name</span>
               </div>
               <div className="relative doctor-dropdown-container">
                 <input
                   type="text"
                   value={formData.doctor}
                   readOnly
                   placeholder={loadingDoctors ? "Loading doctors..." : formData.specialty ? "Select a doctor" : "Select a specialty first"}
                   className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                 />
                 <button 
                   onClick={toggleDoctorDropdown}
                   disabled={doctors.length === 0}
                   className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                 >
                   Change
                 </button>
                 
                 {/* Doctor Dropdown */}
                 {showDoctorDropdown && doctors.length > 0 && (
                   <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                     {doctors.map((doctor) => (
                       <div
                         key={doctor.id}
                         className="px-3 py-2 cursor-pointer hover:bg-blue-100 border-b border-gray-100 last:border-b-0"
                         onClick={() => handleDoctorSelect(doctor)}
                       >
                         <div className="font-medium text-sm">{doctor.firstname} {doctor.lastname}</div>
                         <div className="text-xs text-gray-600">{doctor.specialties}</div>
                       </div>
                     ))}
                   </div>
                 )}
               </div>
             </div>

                         {/* Clinic Location */}
             <div className="space-y-1">
               <div className="flex items-center space-x-2">
                 <MapPin className="h-4 w-4 text-blue-600" />
                 <span className="text-sm font-medium text-gray-600">Clinic Location</span>
               </div>
               <div className="relative clinic-dropdown-container">
                 <input
                   type="text"
                   value={formData.clinic_location}
                   readOnly
                   placeholder={loadingClinics ? "Loading clinics..." : "Select clinic"}
                   className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                 />
                 <button 
                   onClick={toggleClinicDropdown}
                   disabled={availableClinics.length === 0}
                   className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                 >
                   Change
                 </button>
                 
                 {/* Clinic Dropdown */}
                 {showClinicDropdown && availableClinics.length > 0 && (
                   <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                     {availableClinics.map((clinic) => (
                       <div
                         key={clinic.clinic_id}
                         className="px-3 py-2 cursor-pointer hover:bg-blue-100 border-b border-gray-100 last:border-b-0"
                         onClick={() => handleClinicSelect(clinic)}
                       >
                         <div className="font-medium text-sm">{clinic.clinic_name}</div>
                         <div className="text-xs text-gray-600">{clinic.clinic_address}</div>
                         {clinic.is_primary_clinic && (
                           <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                             Primary
                           </span>
                         )}
                       </div>
                     ))}
                   </div>
                 )}
               </div>
             </div>

            {/* Appointment Date */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Appointment Date</span>
                {loadingAvailability && <span className="text-xs text-gray-500">(Loading dates...)</span>}
              </div>
              <select
                value={formData.appointment_date}
                onChange={(e) => handleInputChange('appointment_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                disabled={!formData.doctor_id || loadingAvailability}
              >
                <option value="">
                  {!formData.doctor_id ? 'Select a doctor first' : 
                   loadingAvailability ? 'Loading available dates...' :
                   availableDates.length === 0 && !formData.appointment_date ? 'No available dates' : 'Select a date'}
                </option>
                
                {/* Show current booking date first if it exists and is not in available dates */}
                {formData.appointment_date && !availableDates.includes(formData.appointment_date) && (
                  <option key={formData.appointment_date} value={formData.appointment_date}>
                    {new Date(formData.appointment_date).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </option>
                )}
                
                {/* Show available dates */}
                {availableDates.map((date) => (
                  <option key={date} value={date}>
                    {new Date(date).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </option>
                ))}
              </select>
            </div>

            {/* Appointment Time */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Appointment Time</span>
              </div>
              <select
                value={formData.appointment_time}
                onChange={(e) => handleInputChange('appointment_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                disabled={!formData.appointment_date}
              >
                <option value="">
                  {!formData.appointment_date ? 'Select a date first' :
                   availableTimes.length === 0 && !formData.appointment_time ? 'No available times' : 'Select a time'}
                </option>
                
                {/* Show current booking time first if it exists and is not in available times */}
                {formData.appointment_time && !availableTimes.includes(formData.appointment_time) && (
                  <option key={formData.appointment_time} value={formData.appointment_time}>
                    {formatTimeSlot(formData.appointment_time)}
                  </option>
                )}
                
                {/* Show available times */}
                {availableTimes.map((time) => (
                  <option key={time} value={time}>
                    {formatTimeSlot(time)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status (Read-only) */}
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-blue-600"></div>
              <span className="text-sm font-medium text-gray-600">Status</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                booking.status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : booking.status === 'cancelled' || booking.status === 'Cancelled'
                  ? 'bg-red-100 text-red-800'
                  : booking.status === 'Scheduled'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {booking.status === 'completed' ? 'Completed' : 
                 booking.status === 'cancelled' || booking.status === 'Cancelled' ? 'Cancelled' : 
                 booking.status}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 rounded-b-lg flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Cancel</span>
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditBookingPopup; 